import React from 'react';
import { Space, Tooltip, Typography } from 'antd';
import PropTypes from 'prop-types';

const { Text } = Typography;

export default function CriterionStats({ stats, loading }) {
    if (loading) {
        return (
            <div className="criterion-stats criterion-stats--loading">
                <Text type="secondary">...</Text>
            </div>
        );
    }

    // Guard against incomplete stats objects (can happen during rapid plan switching)
    if (!stats || typeof stats.profitFactor === 'undefined' || !stats.totalTrades) {
        return (
            <div className="criterion-stats criterion-stats--empty">
                <Text type="secondary" style={{ fontSize: 12 }}>
                    No data
                </Text>
            </div>
        );
    }

    const formatPnL = (value) => {
        if (value === null || value === undefined) return '-';
        const formatted = Math.abs(value).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
        return value >= 0 ? `+${formatted}` : `-${formatted}`;
    };

    const formatProfitFactor = (value) => {
        if (value === null || value === undefined) return '-';
        if (value === Infinity || value === 'Infinity') return '\u221E';
        return value.toFixed(1);
    };

    return (
        <Space className="criterion-stats" size="small">
            <Tooltip
                title={`Present in ${stats.presentCount ?? 0} of ${
                    stats.totalEvaluatedTrades ?? 0
                } trades`}
            >
                <Text className="stat-value stat-rate">{stats.presentRate ?? 0}%</Text>
            </Tooltip>

            <Tooltip title="Net Profit/Loss">
                <Text
                    className="stat-value stat-pnl"
                    style={{ color: (stats.netPnL ?? 0) >= 0 ? '#52c41a' : '#ff4d4f' }}
                >
                    {formatPnL(stats.netPnL)}
                </Text>
            </Tooltip>

            <Tooltip title={`Profit Factor (${stats.winCount ?? 0}W / ${stats.lossCount ?? 0}L)`}>
                <Text className="stat-value stat-pf">{formatProfitFactor(stats.profitFactor)}</Text>
            </Tooltip>

            <Tooltip title={`${stats.winCount ?? 0} wins out of ${stats.totalTrades ?? 0} trades`}>
                <Text
                    className="stat-value stat-winrate"
                    style={{ color: (stats.winRate ?? 0) >= 50 ? '#52c41a' : '#faad14' }}
                >
                    {stats.winRate ?? 0}%
                </Text>
            </Tooltip>
        </Space>
    );
}

CriterionStats.propTypes = {
    stats: PropTypes.shape({
        criterionId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        presentRate: PropTypes.number,
        presentCount: PropTypes.number,
        netPnL: PropTypes.number,
        profitFactor: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        winRate: PropTypes.number,
        winCount: PropTypes.number,
        lossCount: PropTypes.number,
        totalTrades: PropTypes.number,
        totalEvaluatedTrades: PropTypes.number
    }),
    loading: PropTypes.bool
};
